import { createClient, type SupabaseClient } from '@supabase/supabase-js';
import type {
  ClientBooking, Membership, OwnerBooking, OwnerBookingsResponse, OwnerContext, SlotsResponse, Stats, Studio,
} from './types';

const url = import.meta.env.VITE_SUPABASE_URL;
const anonKey = import.meta.env.VITE_SUPABASE_ANON_KEY;
export const isConfigured = Boolean(url && anonKey);

/**
 * One browser client. Only the anon/publishable key is ever shipped to the
 * browser; service-role and LLM keys stay on the server.
 */
export const supabase: SupabaseClient = createClient(url || 'http://localhost:54321', anonKey || 'missing-key', {
  auth: {
    storageKey: 'studio-owner-session',
    persistSession: true,
    autoRefreshToken: true,
    detectSessionInUrl: false,
  },
});

const MESSAGES: Record<string, string> = {
  slot_taken: 'Это время только что заняли. Выберите другое.',
  slot_in_past: 'Это время уже прошло или слишком близко. Выберите другое.',
  slot_outside_hours: 'В это время студия не принимает машины.',
  slot_too_far: 'Запись на эту дату пока не открыта.',
  rate_limited: 'Слишком много запросов. Подождите минуту и попробуйте снова.',
  studio_not_found: 'Студия не найдена. Проверьте ссылку.',
  service_not_found: 'Услуга больше недоступна.',
  invalid_phone: 'Проверьте номер телефона.',
  invalid_name: 'Укажите имя.',
  invalid_car: 'Укажите марку и модель машины.',
  idempotency_conflict: 'Форма уже отправлена с другими данными. Обновите страницу.',
  cancel_too_late: 'Онлайн-отмена уже недоступна. Позвоните в студию.',
  cancel_not_allowed: 'Эту запись нельзя отменить онлайн. Позвоните в студию.',
  booking_not_found: 'Запись не найдена.',
  not_authenticated: 'Войдите в кабинет.',
  forbidden: 'Нет доступа к этой студии.',
  refund_exceeds_paid: 'Возврат больше полученной суммы.',
  network: 'Нет связи с сервером. Проверьте интернет.',
  not_configured: 'Сайт ещё не подключён к базе данных.',
};

export class ApiError extends Error {
  code: string;
  constructor(code: string, message: string) {
    super(message);
    this.code = code;
  }
}

export function humanError(e: unknown): string {
  if (e instanceof ApiError) return e.message;
  return 'Что-то пошло не так. Попробуйте ещё раз.';
}

export async function rpc<T>(fn: string, args: Record<string, unknown> = {}): Promise<T> {
  if (!isConfigured) throw new ApiError('not_configured', MESSAGES.not_configured);
  let res;
  try {
    res = await supabase.rpc(fn, args);
  } catch {
    throw new ApiError('network', MESSAGES.network);
  }
  const { data, error } = res;
  if (error) {
    const code = error.code === 'P0001' ? error.message : error.code === '42501' ? 'forbidden' : error.message?.includes('fetch') ? 'network' : error.code ?? 'error';
    const text = MESSAGES[code] ?? (error.code === 'P0001' && error.details ? error.details : 'Что-то пошло не так. Попробуйте ещё раз.');
    throw new ApiError(code, text);
  }
  return data as T;
}

/** Idempotency keys: one per user intent, reused on retries and double taps. */
export const newKey = () => crypto.randomUUID().replace(/-/g, '');

export const api = {
  studio: (slug: string) => rpc<Studio>('get_studio', { p_slug: slug }),
  slots: (slug: string, serviceId: string, from: string, days = 7) =>
    rpc<SlotsResponse>('get_slots', { p_slug: slug, p_service_id: serviceId, p_from: from, p_days: days }),
  createBooking: (a: {
    slug: string; serviceId: string; startsAt: string; name: string; phone: string; car: string; comment?: string; key: string;
  }) =>
    rpc<ClientBooking>('create_booking', {
      p_slug: a.slug, p_service_id: a.serviceId, p_starts_at: a.startsAt, p_customer_name: a.name,
      p_customer_phone: a.phone, p_car: a.car, p_comment: a.comment || null, p_idempotency_key: a.key,
    }),
  myBooking: (token: string) => rpc<ClientBooking | null>('get_my_booking', { p_token: token }),
  cancelMyBooking: (token: string, reason?: string) => rpc<ClientBooking>('cancel_my_booking', { p_token: token, p_reason: reason ?? null }),
  savePush: (token: string, sub: { endpoint: string; p256dh: string; auth: string }) =>
    rpc<{ state: string; run_at?: string }>('save_push_subscription', {
      p_token: token, p_endpoint: sub.endpoint, p_p256dh: sub.p256dh, p_auth: sub.auth,
    }),

  // owner
  memberships: () => rpc<Membership[]>('owner_memberships'),
  ownerContext: (slug: string) => rpc<OwnerContext>('owner_context', { p_slug: slug }),
  ownerBookings: (slug: string, from: string, to: string) =>
    rpc<OwnerBookingsResponse>('owner_bookings', { p_slug: slug, p_from: from, p_to: to }),
  ownerStats: (slug: string, from: string, to: string) => rpc<Stats>('owner_stats', { p_slug: slug, p_from: from, p_to: to }),
  ownerCreateBooking: (a: {
    slug: string; serviceId: string; startsAt: string; name: string; phone: string; car: string; comment?: string;
    resourceId?: string | null; ignoreHours?: boolean; key: string;
  }) =>
    rpc<OwnerBooking>('owner_create_booking', {
      p_slug: a.slug, p_service_id: a.serviceId, p_starts_at: a.startsAt, p_customer_name: a.name,
      p_customer_phone: a.phone, p_car: a.car, p_comment: a.comment || null, p_resource_id: a.resourceId ?? null,
      p_ignore_hours: a.ignoreHours ?? false, p_idempotency_key: a.key,
    }),
  reschedule: (bookingId: string, startsAt: string, resourceId?: string | null, ignoreHours = false) =>
    rpc<OwnerBooking>('owner_reschedule_booking', {
      p_booking_id: bookingId, p_starts_at: startsAt, p_resource_id: resourceId ?? null, p_ignore_hours: ignoreHours,
    }),
  ownerCancel: (bookingId: string, reason?: string) => rpc<OwnerBooking>('owner_cancel_booking', { p_booking_id: bookingId, p_reason: reason ?? null }),
  setStatus: (bookingId: string, status: string) => rpc<OwnerBooking>('owner_set_status', { p_booking_id: bookingId, p_status: status }),
  addPayment: (bookingId: string, kind: 'payment' | 'refund', amount: number, method: string, note: string | null, key: string) =>
    rpc<OwnerBooking>('owner_add_payment', {
      p_booking_id: bookingId, p_kind: kind, p_amount: amount, p_method: method, p_note: note, p_idempotency_key: key,
    }),
  createBlock: (slug: string, resourceId: string, startsAt: string, endsAt: string, note?: string) =>
    rpc<{ id: string }>('owner_create_block', { p_slug: slug, p_resource_id: resourceId, p_starts_at: startsAt, p_ends_at: endsAt, p_note: note ?? null }),
  deleteBlock: (id: string) => rpc('owner_delete_block', { p_block_id: id }),
  updateProfile: (slug: string, patch: Record<string, unknown>) => rpc<OwnerContext>('owner_update_profile', { p_slug: slug, p_patch: patch }),
  upsertService: (slug: string, service: Record<string, unknown>) => rpc<OwnerContext>('owner_upsert_service', { p_slug: slug, p_service: service }),
  upsertResource: (slug: string, resource: Record<string, unknown>) => rpc<OwnerContext>('owner_upsert_resource', { p_slug: slug, p_resource: resource }),
  setHours: (slug: string, hours: { weekday: number; opens: string; closes: string }[]) =>
    rpc<OwnerContext>('owner_set_hours', { p_slug: slug, p_hours: hours }),
  upsertException: (slug: string, day: string, isClosed: boolean, opens: string | null, closes: string | null, note: string | null) =>
    rpc<OwnerContext>('owner_upsert_exception', { p_slug: slug, p_day: day, p_is_closed: isClosed, p_opens: opens, p_closes: closes, p_note: note }),
  deleteException: (slug: string, day: string) => rpc<OwnerContext>('owner_delete_exception', { p_slug: slug, p_day: day }),
  addGalleryItem: (slug: string, path: string, caption: string | null) =>
    rpc<OwnerContext>('owner_add_gallery_item', { p_slug: slug, p_image_path: path, p_caption: caption }),
  replaceGalleryPhoto: (id: string, path: string) => rpc<OwnerContext>('owner_replace_gallery_photo', { p_item_id: id, p_image_path: path }),
  updateGalleryCaption: (id: string, caption: string) => rpc<OwnerContext>('owner_update_gallery_caption', { p_item_id: id, p_caption: caption }),
  deleteGalleryItem: (id: string) => rpc<OwnerContext>('owner_delete_gallery_item', { p_item_id: id }),
};

/** Upload an owner photo into <tenant_id>/<folder>/ (storage RLS checks membership). */
export async function uploadPhoto(tenantId: string, folder: string, file: File): Promise<string> {
  const blob = await downscale(file, 2000);
  const path = `${tenantId}/${folder}/${Date.now()}-${Math.random().toString(36).slice(2, 8)}.${blob.type === 'image/png' ? 'png' : 'jpg'}`;
  const { error } = await supabase.storage.from('studio-media').upload(path, blob, { contentType: blob.type, upsert: false, cacheControl: '31536000' });
  if (error) throw new ApiError('upload', `Не удалось загрузить фото: ${error.message}`);
  return path;
}

/** Shrink big phone photos before upload (keeps PNG logos as PNG). */
async function downscale(file: File, max: number): Promise<Blob> {
  if (!/^image\/(jpeg|png|webp)$/.test(file.type)) throw new ApiError('upload', 'Нужен файл JPG, PNG или WebP.');
  if (file.size > 25 * 1024 * 1024) throw new ApiError('upload', 'Файл больше 25 МБ.');
  const bmp = await createImageBitmap(file).catch(() => null);
  if (!bmp) return file;
  const scale = Math.min(1, max / Math.max(bmp.width, bmp.height));
  if (scale === 1 && file.size < 3 * 1024 * 1024) return file;
  const canvas = new OffscreenCanvas(Math.round(bmp.width * scale), Math.round(bmp.height * scale));
  canvas.getContext('2d')!.drawImage(bmp, 0, 0, canvas.width, canvas.height);
  const type = file.type === 'image/png' ? 'image/png' : 'image/jpeg';
  return canvas.convertToBlob({ type, quality: 0.86 });
}

export function mediaUrl(path: string | null | undefined): string | undefined {
  if (!path) return undefined;
  if (path.startsWith('/') || path.startsWith('http')) return path;
  return `${url}/storage/v1/object/public/studio-media/${path.split('/').map(encodeURIComponent).join('/')}`;
}
